import { useState } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics/track";

type CiteFormat = "apa" | "mla" | "bibtex";

const FORMAT_LABELS: Record<CiteFormat, string> = {
  apa: "APA",
  mla: "MLA",
  bibtex: "BibTeX",
};

/**
 * "Cite this report" card: the citation in three formats plus share links.
 * Every citation string is built here from plain props, so it is server-rendered
 * and stays selectable; the buttons only copy text or open share intents.
 */
export function ReportCitation({
  title,
  year,
  url,
  slug,
  isBg,
}: {
  title: string;
  year?: string;
  url: string;
  slug: string;
  isBg: boolean;
}) {
  const [format, setFormat] = useState<CiteFormat>("apa");
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const locale = isBg ? "bg" : "en";

  const sitePath = (() => {
    try {
      const u = new URL(url);
      return `${u.host}${u.pathname}`;
    } catch {
      return url;
    }
  })();

  const citations: Record<CiteFormat, string> = {
    apa: `Perceptica (${year ?? ""}). ${title}. ${url}`,
    mla: `Perceptica. "${title}." Perceptica, ${year ?? ""}, ${sitePath}.`,
    bibtex: [
      `@report{perceptica-${(year ?? "").toLowerCase() || "nd"}-${slug},`,
      "  author = {Perceptica},",
      `  title  = {${title}},`,
      `  year   = {${year ?? ""}},`,
      `  url    = {${url}}`,
      "}",
    ].join("\n"),
  };
  const text = citations[format];

  const copyCitation = async () => {
    track("report_cite_copy", { slug, format, locale });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable: text stays selectable */
    }
  };

  const copyLink = async () => {
    track("report_share", { slug, network: "copy_link", locale });
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const share = (network: string) => track("report_share", { slug, network, locale });

  const u = encodeURIComponent(url);
  const pill =
    "inline-flex h-9 items-center gap-1.5 rounded-full border border-border/60 bg-background px-3.5 text-[13px] font-medium text-foreground/80 transition-colors hover:border-accent hover:text-accent";
  const tab = (active: boolean) =>
    active
      ? "inline-flex h-8 items-center rounded-full bg-accent px-3.5 text-[12.5px] font-medium text-accent-foreground"
      : "inline-flex h-8 items-center rounded-full border border-border/60 bg-background px-3.5 text-[12.5px] font-medium text-foreground/70 transition-colors hover:border-accent hover:text-accent";

  return (
    <div className="mt-8 rounded-2xl border border-border/60 bg-secondary/40 p-6">
      <h3 className="font-display text-[1.1rem] font-medium text-foreground">
        {isBg ? "Цитирайте доклада" : "Cite this report"}
      </h3>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={isBg ? "Формат на цитата" : "Citation format"}
        >
          {(Object.keys(citations) as CiteFormat[]).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={format === f}
              onClick={() => {
                setFormat(f);
                setCopied(false);
              }}
              className={tab(format === f)}
            >
              {FORMAT_LABELS[f]}
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={copyCitation}
          className="w-full shrink-0 sm:w-auto"
          aria-live="polite"
        >
          {copied ? <Check /> : <Copy />}
          {copied ? (isBg ? "Копирано" : "Copied") : isBg ? "Копирайте" : "Copy"}
        </Button>
      </div>

      <pre className="mt-3 w-full select-all overflow-x-auto break-words whitespace-pre-wrap rounded-lg border border-border/60 bg-background px-3.5 py-3 font-mono text-[12.5px] leading-relaxed text-foreground">
        {text}
      </pre>

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border/60 pt-5">
        <span className="mr-1 text-[13px] font-medium text-muted-foreground">
          {isBg ? "Споделете" : "Share"}
        </span>
        <a
          className={pill}
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => share("linkedin")}
        >
          LinkedIn
        </a>
        <a
          className={pill}
          href={`https://x.com/intent/post?url=${u}&text=${encodeURIComponent(title)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => share("x")}
        >
          X
        </a>
        <button type="button" className={pill} onClick={copyLink}>
          {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
          <span aria-live="polite">
            {linkCopied
              ? isBg
                ? "Линкът е копиран"
                : "Link copied"
              : isBg
                ? "Копирайте линка"
                : "Copy link"}
          </span>
        </button>
      </div>

      <p className="mt-4 text-[13.5px] text-muted-foreground">
        {isBg
          ? "Журналисти: можете да цитирате данните с линк към тази страница."
          : "Journalists: you may quote these findings with a link to this page."}
      </p>
    </div>
  );
}
